import { CommonState } from '@/common/store/modules/common/types/CommonState';
import { NfsNode } from '@/common/store/modules/nfsCluster';
import {
  NFS_SHARE_STATUS_TEXT,
  NFS_SHARE_STATUSES,
  NFS_SHARE_STATUSES_ICONS_MAP
} from '@/common/constants/nfsShare.constants';
import { TaskState } from '@schema';
import { groupBy } from 'lodash-es';
import { STATE_ICON_DISABLED, STATE_ICON_ENABLED } from '@/common/utils/statuses';
import { enabledDisabledFormatter } from '@/common/utils/formatter.bool';
import { TaskName } from '@/common/constants/taskNames.constants';
import getClusterId from '@/common/store/pinia/storageCluster/getClusterId';

export type NfsShareRaw = {
  id: string,
  exports: Array<unknown>
  failure_domain: string
  has_krb_keytab: boolean
  ip_address: string
  krb_auth_enabled: boolean
  name: string
  node: NfsNode
  path_prefix: string
  redundancy: Record<string, any>
  space: Record<string, any>
  status: NFS_SHARE_STATUSES
  tasks: Array<string>
  tier: number
}

export type NfsShare = NfsShareRaw & {
  statusText: string,
  statusIcon: string,
  nodeName: string,
  exportCount: number,
  authStatusIcon: string,
  authStatusText: string
}

function resource() {
  return getClusterId()
    .then(clusterId => (`/api/v2/${clusterId}/nfs/shares`));
}

const nfsShareGetters: CommonGetterTree<CommonState> = {
  _mapItem: (state, getters, rootState, rootGetters) => (id: ID): NfsShare => {
    const item: NfsShareRaw = state.itemsMap[id];
    return {
      ...item,
      statusText: NFS_SHARE_STATUS_TEXT[item.status],
      statusIcon: NFS_SHARE_STATUSES_ICONS_MAP[item.status],
      authStatusIcon: item.krb_auth_enabled ? STATE_ICON_ENABLED : STATE_ICON_DISABLED,
      authStatusText: enabledDisabledFormatter(item.krb_auth_enabled),
      nodeName: rootGetters['locations/getNodeById'](item.node.id)?.name,
      exportCount: item.exports.length
    };
  },
  getSharesByNodes: (state, getters): Record<string, Array<NfsShare>> => groupBy(getters.getItems, (share: NfsShare) => share.node.id),
  getSharesByNode: (_state, getters) => (nodeId: ID): Array<NfsShare> => getters.getItems.filter((share: NfsShare) => share.node.id === nodeId),
  getExportsCount: (state) => shareName => state.itemsMap[shareName]?.exports.length
};

const nfsShareActions: CommonActionTree<CommonState> = {
  CREATE(_, payload: FormData) {
    return getClusterId()
      .then(clusterId => this.$api.upload(`/api/v2/${clusterId}/nfs/shares`, payload, { method: 'POST' }));
  },
  UPDATE(_, { payload, shareName }) {
    return getClusterId()
      .then(clusterId => this.$api.upload(`/api/v2/${clusterId}/nfs/shares/${shareName}`, payload, { method: 'PUT' }));
  },
  START({ commit }, shareName: string) {
    return getClusterId()
      .then(clusterId => this.$api.post(`/api/v2/${clusterId}/nfs/shares/${shareName}/start`))
      .then(() => {
        commit('CHANGE', { item: { id: shareName, status: NFS_SHARE_STATUSES.updating } });
      });
  },
  STOP({ commit }, shareName: string) {
    return getClusterId()
      .then(clusterId => this.$api.post(`/api/v2/${clusterId}/nfs/shares/${shareName}/stop`))
      .then(() => {
        commit('CHANGE', { item: { id: shareName, status: NFS_SHARE_STATUSES.stopping } });
      });
  },
  socket_tasks({ commit, dispatch }, { name, state, result, args }) {
    if (name === TaskName.NFS_SHARE_ADD && state === TaskState.SUCCESS) {
      dispatch('FETCH_ITEM', { id: result.name });
    }

    if (!args?.length) return;
    const [_clusterId, shareName] = args;

    if (name === TaskName.NFS_SHARE_UPDATE && state === TaskState.SUCCESS) {
      commit('CHANGE', { item: { id: shareName, ...result } });
    }
    if (name === TaskName.NFS_SHARE_START && state === TaskState.SUCCESS) {
      commit('CHANGE', { item: { id: shareName, status: NFS_SHARE_STATUSES.running } });
    }
    if (name === TaskName.NFS_SHARE_STOP && state === TaskState.SUCCESS) {
      commit('CHANGE', { item: { id: shareName, status: NFS_SHARE_STATUSES.stopped } });
    }
  }
};

export default new class implements CommonStore<CommonState, NfsShare> {
  name = 'nfsShares';
  resource = resource;

  getters = nfsShareGetters;

  actions = nfsShareActions;

  plural = data => data.data.map(item => ({ ...item, id: item.name }));

  singular = data => ({ ...data, id: data.name });

  config = {
    needWaitTask: true
  };
}();
